import { useCallback, useEffect, useState } from "react";
import "./list-user.css";
import { useUser } from "../../../contexts/user-context";
import { useEthosNotification } from "../../../contexts/ethos-notification-context";
import { UserBox } from "./UserBox";
import { useService } from "../../../contexts/service-context";
import { Service } from "../../../models";
import { useEthosModal } from "../../../contexts/ethos-modal-context";
import { UserCreationFormValues } from "../../../ui-components/ethos-modal/user-create-content/user-create-content";
import { useAuthenticator } from "@aws-amplify/ui-react";
import ExportUsersModal from "./components/export-users-data-modal";

export interface LightService {
  id: string;
  name: string;
}

export enum AVAILABLE_USER_PLATFORM_ROLE {
  ADMIN = "ADMIN",
  AVVOCATO = "AVVOCATO",
  AGENTE = "AGENTE",
  CLIENTE = "CLIENTE",
  SALES = "SALES",
  BROKER = "BROKER",
  PRODUZIONE = "PRODUZIONE",
  DEFAULT = "DEFAULT",
}

export interface FeeType {
  serviceId: string;
  percentage: string;
}
export interface PlatformUser {
  comp: string | null;
  isActive: boolean;
  isVerified: boolean;
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  note?: string;
  username: string;
  servicesCompensations: FeeType[];
  role: AVAILABLE_USER_PLATFORM_ROLE;
  created_at: string;
  updated_at: string;
}

const ListUsers = () => {
  const { user: cognitoUser } = useAuthenticator();

  const role =
    (cognitoUser as unknown as any)?.signInUserSession?.accessToken?.payload?.[
      "cognito:groups"
    ]?.[0] ?? "CLIENTE";

  const currentUserName =
    (cognitoUser as unknown as any)?.signInUserSession?.accessToken?.payload
      ?.username ?? "Unknown User";

  const isAdmin = role === "ADMIN";

  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [services, setServices] = useState<LightService[]>([]);
  const {
    searchUsers: getUsers,
    newUser,
    editUser,
    disabledUser,
    enableUser,
  } = useUser();
  const { getServices } = useService();
  const { handleError } = useEthosNotification();
  const { openCreateUserContent, openEthosConfirmModal } = useEthosModal();
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [showDisableUser, setShowDisableUser] = useState(true);

  const getUserList = useCallback(
    async (searchTerm?: string, showDisableUser?: boolean) => {
      const { error, data } = await getUsers(
        searchTerm,
        null,
        !showDisableUser ? null : true,
      );

      if (data.error) {
        handleError(error);
        return;
      }

      setUsers(data.data);
    },
    [getUsers, handleError],
  );

  const doCreateUser = async (user: UserCreationFormValues) => {
    const { data, error } = await newUser(user);

    if (error) {
      handleError(error);
      return;
    }
    const createdUser = data?.data;
    if (createdUser) {
      setUsers((prev) => [createdUser, ...prev]);
    } else {
      await getUserList();
    }
  };

  const doUpdateUser = async (userId: string, updatedUser: PlatformUser) => {
    const { error } = await editUser(userId, updatedUser);
    if (error) {
      handleError(error);
      return;
    }

    setUsers((prev) => prev.map((u) => (u._id === userId ? updatedUser : u)));
  };

  const doUserDelete = async (userId: string) => {
    const doDelete = async () => {
      const { error } = await disabledUser(userId);
      if (error) {
        handleError(error);
        return;
      }

      getUserList(searchTerm, showDisableUser);
    };

    openEthosConfirmModal(
      "Sicuro di voler disabilitare l'utente, una volta completata l'operazione l'utente non potrà più accedere alla piattaforma ma i suoi dati rimarranno salvati e potrai riattivarlo in qualsiasi momento",
      doDelete,
      null,
      "Conferma disabilitazione",
    );
  };

  const doUserRestore = async (userId: string) => {
    const doRestore = async () => {
      const { error } = await enableUser(userId);
      if (error) {
        handleError(error);
        return;
      }
      getUserList(searchTerm, showDisableUser);
    };
    openEthosConfirmModal(
      "Sicuro di voler riabilitare l'utente, una volta completata l'operazione l'utente potrà accedere nuovamente alla piattaforma",
      doRestore,
      null,
      "Conferma riattivazione",
    );
  };

  const loadServices = useCallback(async () => {
    try {
      const { data, error } = await getServices();

      if (error) {
        handleError(error);
        return;
      }

      const mappedService = (data.data as Service[])
        .filter((s) => !s.disabled)
        .map((s) => {
          return {
            id: s._id,
            name: s.displayName,
          };
        }) as LightService[];

      setServices(mappedService);
    } catch (err) {
      handleError(err);
    }
  }, [getServices, handleError]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getUserList(searchTerm, showDisableUser);
    }, 300);

    return () => clearTimeout(timer);
  }, [getUserList, searchTerm, showDisableUser]);

  useEffect(() => {
    if (!isAdmin) {
      window.location.pathname = "/home";
    }
  }, [isAdmin]);

  return (
    <div className="list-users-page">
      <div className="row mb-3">
        <div className="col-12 text-center">
          <h3>Lista degli utenti</h3>
          <div className="d-flex flex-row gap-3  mt-3">
            <input
              type="text"
              placeholder="Cerca utente..."
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
            />
            <div
              className={`chip-toggle ${showDisableUser ? "active" : ""}`}
              onClick={() => setShowDisableUser((prev) => !prev)}
            >
              {showDisableUser ? "Disattivati visibili" : "Mostra disattivati"}
            </div>
            {isAdmin && (
              <>
                <button
                  className="btn btn-outline-primary"
                  disabled={!isAdmin}
                  onClick={() => {
                    openCreateUserContent(doCreateUser);
                  }}
                >
                  Crea nuovo utente
                </button>
                <button
                  className="btn btn-outline-success"
                  disabled={!isAdmin}
                  onClick={() => setShowExportModal(true)}
                >
                  Esporta Report Utenti
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="root">
        <UserBox
          headerTitle="ADMIN"
          role={AVAILABLE_USER_PLATFORM_ROLE.ADMIN}
          users={users}
          services={services}
          doUserEdit={doUpdateUser}
          doUserDelete={doUserDelete}
          doUserRestore={doUserRestore}
          isAdmin={isAdmin || false}
          currentUsername={currentUserName}
        />
        <UserBox
          headerTitle="PRODUZIONE"
          role={AVAILABLE_USER_PLATFORM_ROLE.PRODUZIONE}
          users={users}
          services={services}
          doUserEdit={doUpdateUser}
          doUserDelete={doUserDelete}
          doUserRestore={doUserRestore}
          isAdmin={isAdmin || false}
          currentUsername={currentUserName}
        />
        <UserBox
          headerTitle="SALES"
          role={AVAILABLE_USER_PLATFORM_ROLE.SALES}
          users={users}
          services={services}
          doUserEdit={doUpdateUser}
          doUserDelete={doUserDelete}
          doUserRestore={doUserRestore}
          isAdmin={isAdmin || false}
          currentUsername={currentUserName}
        />
        <UserBox
          headerTitle="SEGNALATORE"
          role={AVAILABLE_USER_PLATFORM_ROLE.BROKER}
          users={users}
          services={services}
          doUserEdit={doUpdateUser}
          doUserDelete={doUserDelete}
          doUserRestore={doUserRestore}
          isAdmin={isAdmin || false}
          currentUsername={currentUserName}
        />
      </div>
      {showExportModal && (
        <ExportUsersModal
          users={users}
          isAdmin={isAdmin}
          closeModal={(value) => setShowExportModal(value)}
        />
      )}
    </div>
  );
};

export default ListUsers;
